import * as React from 'react';
import { useState, useMemo, useEffect, useCallback } from 'react';
import { Loader2, RotateCcw, History, ChevronDown } from 'lucide-react';
import { PageHeader } from '../../components/page-header';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '../../components/ui/tabs';
import { useToast } from '../../components/ui/toast';
import { cn } from '../../lib/utils';
import fetchAIPrompts from '../../../application/routes/settings/utils/fetchAIPrompts';
import fetchAIPromptHistory from '../../../application/routes/settings/utils/fetchAIPromptHistory';
import { saveAIPrompt } from '../../../application/routes/settings/utils/saveAIPrompt';
import { promoteToProduction } from '../../../application/routes/settings/utils/promoteToProduction';

declare const global: {
  me: {
    id: string;
    orgID: string;
    imageName?: string;
    name: string;
  };
};

interface AITraining {
  id: string;
  orgID: string;
  service: string;
  name: string;
  description?: string;
  prompt: string;
  revision: number;
  status?: string;
  users?: string[];
  public?: boolean;
  createdBy?: string;
  lastUpdatedBy?: string;
  createdAt: string;
  updatedAt: string;
}

// ─── Version History ────────────────────────────────────────────────────────

function VersionHistory({
  service,
  history,
  isLoading,
  isSaving,
  onPromote,
}: {
  service: string;
  history: AITraining[];
  isLoading: boolean;
  isSaving: boolean;
  onPromote: (promptId: string, service: 'askRecorder' | 'askPaiv') => void;
}) {
  const [isOpen, setIsOpen] = useState(false);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const truncateText = (text: string, maxLength: number = 100) => {
    if (text.length <= maxLength) {
      return text;
    }
    return text.substring(0, maxLength) + '...';
  };

  const latestVersion =
    history.length > 0
      ? history.reduce((latest, current) =>
          current.revision > latest.revision ? current : latest
        )
      : null;

  return (
    <div className="border border-border rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 hover:bg-muted/30 transition-colors"
      >
        <div className="flex items-center gap-2">
          <History className="size-4 text-foreground" />
          <span className="font-semibold text-foreground">Version History</span>
          {history.length > 0 && (
            <Badge variant="outline" className="text-xs">
              {history.length} versions
            </Badge>
          )}
        </div>
        <ChevronDown
          className={cn(
            'size-4 text-muted-foreground transition-transform',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {isOpen && (
        <div className="border-t border-border p-4">
          {isLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="size-5 animate-spin text-muted-foreground" />
            </div>
          ) : history.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No previous versions yet. Save your first prompt to begin tracking
              versions.
            </p>
          ) : (
            <div className="space-y-3">
              {history.map((version) => {
                const isLatest =
                  latestVersion && version.revision === latestVersion.revision;
                const isProduction = version.status === 'production';
                const isDraft = version.status === 'draft';

                return (
                  <div
                    key={version.id}
                    className="flex items-start justify-between gap-3 p-3 rounded-lg border border-border"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <Badge className="bg-[#35829E] text-white text-xs">
                          v{version.revision.toFixed(1)}
                        </Badge>
                        {isProduction && (
                          <Badge className="bg-green-600 text-white text-xs">
                            Production
                          </Badge>
                        )}
                        {isDraft && (
                          <Badge variant="secondary" className="text-xs">
                            Draft
                          </Badge>
                        )}
                        {isLatest && !isProduction && (
                          <Badge className="bg-primary text-primary-foreground text-xs">
                            Latest
                          </Badge>
                        )}
                        <span className="text-xs text-muted-foreground">
                          {formatDate(version.updatedAt)}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground truncate">
                        {truncateText(version.prompt)}
                      </p>
                    </div>
                    <Button
                      variant={isProduction ? 'secondary' : 'outline'}
                      size="sm"
                      disabled={isProduction || isSaving}
                      onClick={() =>
                        onPromote(
                          version.id,
                          service as 'askRecorder' | 'askPaiv'
                        )
                      }
                      className="shrink-0"
                    >
                      {isProduction ? 'Production' : 'Set as Production'}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────

export function AISettingsPage() {
  const [prompts, setPrompts] = useState<AITraining[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [askRecorderPrompt, setAskRecorderPrompt] = useState('');
  const [originalAskRecorderPrompt, setOriginalAskRecorderPrompt] =
    useState('');
  const [isSaving, setIsSaving] = useState(false);

  const [askRecorderHistory, setAskRecorderHistory] = useState<AITraining[]>(
    []
  );
  const [historyLoading] = useState(false);

  const toast = useToast();

  const loadInitialData = useCallback(async () => {
    try {
      setIsLoading(true);
      const orgID = (global as any)?.me?.orgID;
      if (!orgID) {
        setError('Organization not found');
        return;
      }

      const [promptsData, historyData] = await Promise.all([
        fetchAIPrompts(orgID),
        fetchAIPromptHistory(orgID, 'askRecorder'),
      ]);

      setPrompts(promptsData);
      setAskRecorderHistory(historyData);
      setError(null);
    } catch (err) {
      console.error('Error loading prompts:', err);
      setError('Failed to load prompts');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const productionPrompt = useMemo(() => {
    return prompts.find((p) => p.service === 'askRecorder');
  }, [prompts]);

  useEffect(() => {
    if (productionPrompt) {
      const prompt = productionPrompt.prompt || '';
      setAskRecorderPrompt(prompt);
      setOriginalAskRecorderPrompt(prompt);
    }
  }, [productionPrompt]);

  const hasChanges =
    askRecorderPrompt.trim() !== originalAskRecorderPrompt.trim();

  const handleReset = () => {
    setAskRecorderPrompt(originalAskRecorderPrompt);
  };

  const handleSave = async () => {
    if (!askRecorderPrompt.trim()) {
      toast.error('Prompt cannot be empty');
      return;
    }

    setIsSaving(true);
    try {
      await saveAIPrompt('askRecorder', askRecorderPrompt);
      setOriginalAskRecorderPrompt(askRecorderPrompt);
      toast.success('Prompt saved successfully');

      // Reload data
      const orgID = (global as any)?.me?.orgID;
      const [promptsData, historyData] = await Promise.all([
        fetchAIPrompts(orgID),
        fetchAIPromptHistory(orgID, 'askRecorder'),
      ]);
      setPrompts(promptsData);
      setAskRecorderHistory(historyData);
    } catch (err) {
      console.error('Error saving prompt:', err);
      toast.error('Failed to save prompt');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePromote = async (
    promptId: string,
    service: 'askRecorder' | 'askPaiv'
  ) => {
    setIsSaving(true);
    try {
      await promoteToProduction(promptId, service);
      toast.success('Prompt promoted to production');

      // Reload data
      const orgID = (global as any)?.me?.orgID;
      const [promptsData, historyData] = await Promise.all([
        fetchAIPrompts(orgID),
        fetchAIPromptHistory(orgID, service),
      ]);
      setPrompts(promptsData);
      if (service === 'askRecorder') {
        setAskRecorderHistory(historyData);
      }
    } catch (err) {
      console.error('Error promoting prompt:', err);
      toast.error('Failed to promote prompt');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        <PageHeader
          title="AI Settings"
          avatar={{ src: global.me?.imageName, name: global.me?.name }}
        />
        <div className="flex items-center justify-center flex-1">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col h-full">
        <PageHeader
          title="AI Settings"
          avatar={{ src: global.me?.imageName, name: global.me?.name }}
        />
        <div className="flex items-center justify-center flex-1">
          <p className="text-destructive">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="AI Settings"
        avatar={{ src: global.me?.imageName, name: global.me?.name }}
      />

      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        <div className="max-w-3xl mx-auto">
          <div className="mb-6">
            <h2 className="text-lg font-bold text-foreground">
              AI Prompts Management
            </h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              Configure AI prompts for different services in your organization.
              These prompts will be used to provide context and instructions to
              AI features.
            </p>
          </div>

          <Tabs defaultValue="askPaiv">
            <TabsList>
              <TabsTrigger value="askPaiv">Ask Paiv</TabsTrigger>
            </TabsList>

            <TabsContent value="askPaiv" className="mt-4 space-y-4">
              {/* Prompt Editor */}
              <div className="border border-border rounded-xl p-5 space-y-4">
                <div>
                  <h3 className="text-base font-semibold text-foreground">
                    Ask Paiv Service
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    This prompt will be used to define how AI will access audio
                    transcriptions to help coach users or allow them to ask
                    questions about specific recordings.
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground mb-1.5 block">
                    Ask Recorder Prompt
                  </label>
                  <textarea
                    rows={8}
                    placeholder="Enter the prompt that will be used for Ask Recorder AI processing..."
                    value={askRecorderPrompt}
                    onChange={(e) => setAskRecorderPrompt(e.target.value)}
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-ring resize-y min-h-[120px]"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleReset}
                    disabled={isSaving || !hasChanges}
                  >
                    <RotateCcw className="size-3.5 mr-1.5" />
                    Reset to Original
                  </Button>

                  <Button
                    size="sm"
                    onClick={handleSave}
                    disabled={isSaving || !hasChanges}
                  >
                    {isSaving ? (
                      <Loader2 className="size-4 animate-spin mr-1.5" />
                    ) : null}
                    {isSaving ? 'Saving...' : 'Save Prompt'}
                  </Button>
                </div>
              </div>

              {/* Version History */}
              <VersionHistory
                service="askRecorder"
                history={askRecorderHistory}
                isLoading={historyLoading}
                isSaving={isSaving}
                onPromote={handlePromote}
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
